import type { AdmissionTaskPrefillMapping, AdmissionTaskPrefillPayload } from "@/src/hooks/useAdmissionTaskPrefill";
import type { IApplicantUpdatePayload } from "@/src/types/applicant";
import type { ITaskUpdatePayload } from "@/src/api/tasksPageRequests";

type ApproveFieldTransform = (value: unknown) => string;

export interface AOPApproveApplicantFieldMap {
  formField: string;
  apiKey: keyof IApplicantUpdatePayload;
  transform?: ApproveFieldTransform;
}

export interface AOPApproveTaskFieldMap {
  formField: string;
  apiKey: keyof ITaskUpdatePayload;
  transform?: ApproveFieldTransform;
}

export const normalizeDateForInput = (value: unknown): string => {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const ddmmyyyy = trimmed.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (ddmmyyyy) return `${ddmmyyyy[3]}-${ddmmyyyy[2]}-${ddmmyyyy[1]}`;
  return "";
};

/**
 * AOP form field → PUT /applicants/{applicant_id} body key.
 * Only fields that exist on Admissions Office forms are listed.
 */
export const AOP_APPROVE_APPLICANT_FIELD_MAP: AOPApproveApplicantFieldMap[] = [
  { formField: "fullName", apiKey: "fullname" },
  { formField: "fullNameNat", apiKey: "fullname_nat" },
  { formField: "fullNameEn", apiKey: "fullname_nat" },
  { formField: "dateOfBirth", apiKey: "birth_date", transform: normalizeDateForInput },
  { formField: "nationality", apiKey: "nationality" },
  /** Form label "Selected course*" → PUT /applicants body key `course` */
  { formField: "selectedCourse", apiKey: "course" },
];

/**
 * AOP form field → PUT /tasks/update/{task_id} body key.
 * `status` and `attached_file` are added separately on approve.
 */
export const AOP_APPROVE_TASK_FIELD_MAP: AOPApproveTaskFieldMap[] = [
  { formField: "whose_doc", apiKey: "whoose" },
  { formField: "type", apiKey: "type" },
  { formField: "formOfEducation", apiKey: "form_of_education" },
  { formField: "admissionSemester", apiKey: "admission_semester" },
  { formField: "intake", apiKey: "intake" },
  { formField: "category", apiKey: "category" },
  { formField: "mediumOfInstruction", apiKey: "medium_of_instruction" },
  { formField: "duration", apiKey: "duration" },
  { formField: "tuitionFee", apiKey: "tuition_fee" },
  { formField: "medicalInsurance", apiKey: "medical_insurance" },
  { formField: "residencePermit", apiKey: "residence_permit" },
  { formField: "otherExpenses", apiKey: "other_expenses" },
  { formField: "acommodation", apiKey: "acommodation" },
  { formField: "admissionFee", apiKey: "admission_fee" },
  { formField: "completeOneYear", apiKey: "complete_one_year" },
  /** Form label "Selected course*" → PUT /tasks/update body key `selected_course` */
  { formField: "selectedCourse", apiKey: "selected_course" },
  { formField: "transfer", apiKey: "transfer_fee" },
];

const studentIdTransform = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  return String(value);
};

/** Fields shared by most Admissions Office document forms. */
export const standardCourseDocumentTaskPrefillMapping: AdmissionTaskPrefillMapping[] = [
  { formField: "whose_doc", payloadKey: "whoose" },
  { formField: "fullName", payloadKey: "fullName" },
  { formField: "fullNameNat", payloadKey: "fullNameNat" },
  { formField: "dateOfBirth", payloadKey: "dateOfBirth", transform: normalizeDateForInput },
  { formField: "nationality", payloadKey: "nationality" },
  { formField: "selectedCourse", payloadKey: "selectedCourse" },
  { formField: "mediumOfInstruction", payloadKey: "mediumOfInstruction" },
  { formField: "duration", payloadKey: "duration" },
  { formField: "task_id", payloadKey: "task_id" },
  { formField: "applicant_id", payloadKey: "applicant_id", transform: studentIdTransform },
];

/** Admission letter and similar forms with extra task fields. */
export const admissionLetterTaskPrefillMapping: AdmissionTaskPrefillMapping[] = [
  ...standardCourseDocumentTaskPrefillMapping.filter((item) => item.payloadKey !== "task_id"),
  { formField: "formOfEducation", payloadKey: "formOfEducation" },
  { formField: "admissionSemester", payloadKey: "admissionSemester" },
  { formField: "intake", payloadKey: "intake" },
  { formField: "category", payloadKey: "category" },
  { formField: "tuitionFee", payloadKey: "tuitionFee" },
  { formField: "medicalInsurance", payloadKey: "medicalInsurance" },
  { formField: "residencePermit", payloadKey: "residencePermit" },
  { formField: "otherExpenses", payloadKey: "otherExpenses" },
  { formField: "acommodation", payloadKey: "acommodation" },
  { formField: "admissionFee", payloadKey: "admissionFee" },
  { formField: "transfer", payloadKey: "transfer" },
  { formField: "completeOneYear", payloadKey: "completeOneYear" },
  { formField: "task_id", payloadKey: "task_id" },
  { formField: "student_id", payloadKey: "student_id", transform: studentIdTransform },
  { formField: "applicant_id", payloadKey: "applicant_id", transform: studentIdTransform },
];

/** Ukraine request letter — fewer shared fields. */
export const requestLetterTaskPrefillMapping: AdmissionTaskPrefillMapping[] = [
  { formField: "whose_doc", payloadKey: "whoose" },
  { formField: "fullName", payloadKey: "fullName" },
  { formField: "fullNameEn", payloadKey: "fullNameNat" },
  { formField: "dateOfBirth", payloadKey: "dateOfBirth", transform: normalizeDateForInput },
  { formField: "task_id", payloadKey: "task_id" },
  { formField: "applicant_id", payloadKey: "applicant_id", transform: studentIdTransform },
];

export const certificateTaskPrefillMapping: AdmissionTaskPrefillMapping[] = [
  { formField: "whose_doc", payloadKey: "whoose" },
  { formField: "fullName", payloadKey: "fullName" },
  { formField: "task_id", payloadKey: "task_id" },
  { formField: "applicant_id", payloadKey: "applicant_id", transform: studentIdTransform },
];

export const syncCourseAndNationalityAutocompletes = (
  payload: AdmissionTaskPrefillPayload,
  _offeredCourseAuto: { onSelect: (value: string) => void },
  nationalitiesAuto: { onSelect: (value: string) => void },
  whoseDocAuto?: { onSelect: (value: string) => void },
): void => {
  if (typeof payload.nationality === "string" && payload.nationality.trim() !== "") {
    nationalitiesAuto.onSelect(payload.nationality);
  }
  if (whoseDocAuto && typeof payload.whoose === "string" && payload.whoose.trim() !== "") {
    whoseDocAuto.onSelect(payload.whoose);
  }
};

export const syncWhoseDocAutocomplete = (
  payload: AdmissionTaskPrefillPayload,
  whoseDocAuto: { onSelect: (value: string) => void },
): void => {
  if (typeof payload.whoose === "string" && payload.whoose.trim() !== "") {
    whoseDocAuto.onSelect(payload.whoose);
  }
};
